// Pure helpers for the combo admin UI: money parsing/formatting, tier IDs,
// option price override precedence and validation, and the combo price /
// points preview. Kept free of React, antd and i18n so they can be unit
// tested in isolation (e2e/comboPricing.spec.ts).
//
// The rules mirror the backend (services/api/rest/combo_schemas.go and
// menu_combo.go); see backend/docs/COMBO_MENU_DESIGN.md.

import type {
  ItemModificationChoice,
  ItemModificationSchema,
  ItemSchema,
  ItemSchemaGroup,
} from "../../types/model";
import type { PointsConfig } from "../points-config/types";
import { effectivePoints } from "../points-config/points";
import { uuidToTrid } from "../../shared/utils/trid";
import type {
  ComboAddonModificationPrice,
  ComboAddonSchema,
  ComboAddonTier,
  ComboSchema,
} from "./types";

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

const DOLLAR_INPUT = /^\s*(-|\+)?(\d*)(?:\.(\d*))?\s*$/;

/**
 * Parses a dollar amount typed by the admin ("2", "2.5", "-0.25", ".75") into
 * integer cents. Digits past the second decimal place are truncated, like
 * Price.fromString. Returns null for blank or unparseable input, so callers
 * can treat blank as "no value" (e.g. "inherit" for overrides).
 */
export function dollarsToCents(input: string | number | null | undefined): number | null {
  if (input === null || input === undefined) {
    return null;
  }
  const s = typeof input === "number" ? input.toString() : input;
  const m = DOLLAR_INPUT.exec(s);
  if (!m) {
    return null;
  }
  const [, sign, whole = "", frac = ""] = m;
  if (whole === "" && frac === "") {
    return null;
  }
  const cents = parseInt(whole || "0", 10) * 100 + parseInt((frac + "00").slice(0, 2), 10);
  return sign === "-" && cents !== 0 ? -cents : cents;
}

/** Cents to the string an input shows, e.g. 1234 -> "12.34", -50 -> "-0.50". */
export function centsToDollars(cents: number): string {
  const abs = Math.abs(Math.trunc(cents));
  const s = `${Math.trunc(abs / 100)}.${(abs % 100).toString().padStart(2, "0")}`;
  return cents < 0 ? `-${s}` : s;
}

/** Cents for display, e.g. 1234 -> "$12.34", -50 -> "-$0.50". */
export function formatCents(cents: number): string {
  const s = centsToDollars(Math.abs(cents));
  return cents < 0 ? `-$${s}` : `$${s}`;
}

/** A price adjustment for display, always signed: "+$0.50", "-$0.25", "+$0.00". */
export function formatAdjustment(cents: number): string {
  return cents < 0 ? formatCents(cents) : `+${formatCents(cents)}`;
}

// ---------------------------------------------------------------------------
// IDs
// ---------------------------------------------------------------------------

function randomUuid(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }
  // crypto.randomUUID needs a secure context; fall back to a v4 UUID built
  // from getRandomValues.
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * A new typed ID in the backend's trid format: "<tag>_" + unpadded base64url
 * of the UUID bytes (lib/trid).
 */
export function newTridId(tag: string): string {
  return uuidToTrid(randomUuid(), tag);
}

/**
 * A client-generated combo add-on tier ID. The backend keeps client-supplied
 * tier IDs, which lets a tier-scoped option override reference a tier that
 * hasn't been saved yet.
 */
export function newTierId(): string {
  return newTridId("cbt");
}

// ---------------------------------------------------------------------------
// Menu index
// ---------------------------------------------------------------------------

export type MenuItemRef = {
  item: ItemSchema;
  group: ItemSchemaGroup;
};

export type MenuItemIndex = Map<string, MenuItemRef>;

export function indexMenuItems(groups: ReadonlyArray<ItemSchemaGroup>): MenuItemIndex {
  const idx: MenuItemIndex = new Map();
  for (const group of groups) {
    for (const item of group.item_schemas ?? []) {
      idx.set(item.id, { item, group });
    }
  }
  return idx;
}

// ---------------------------------------------------------------------------
// Option price overrides
// ---------------------------------------------------------------------------

/** Scope of an override: a tier ID, or ALL_TIERS for the add-on-wide row. */
export const ALL_TIERS = "*";

/** Key of an override input in the editor: "<tier ID or *>|<choice ID>". */
export function overrideKey(tierId: string | undefined | null, choiceId: string): string {
  return `${tierId ?? ALL_TIERS}|${choiceId}`;
}

/** Raw override inputs as the editor holds them, keyed by overrideKey. */
export type OverrideInputs = Record<string, string>;

export function overridesToInputs(
  overrides: ReadonlyArray<ComboAddonModificationPrice>
): OverrideInputs {
  const inputs: OverrideInputs = {};
  for (const mp of overrides) {
    inputs[overrideKey(mp.combo_addon_tier_id, mp.item_modification_choice_id)] = centsToDollars(
      mp.price_adjustment
    );
  }
  return inputs;
}

/** Modification choices offered by each tier's items, keyed by tier ID then choice ID. */
export function offeredChoicesByTier(
  tiers: ReadonlyArray<ComboAddonTier>,
  items: MenuItemIndex
): Map<string, Map<string, ItemModificationChoice>> {
  const result = new Map<string, Map<string, ItemModificationChoice>>();
  for (const tier of tiers) {
    const choices = new Map<string, ItemModificationChoice>();
    for (const ti of tier.items) {
      const ref = items.get(ti.item_schema_id);
      for (const ms of ref?.item.modification_schemas ?? []) {
        for (const c of ms.choices ?? []) {
          choices.set(c.id, c);
        }
      }
    }
    result.set(tier.id, choices);
  }
  return result;
}

export type OfferedChoice = {
  choice: ItemModificationChoice;
  // Tiers with at least one item offering the choice, in tier order.
  tierIds: string[];
};

export type OfferedModificationSchema = {
  schema: ItemModificationSchema;
  choices: OfferedChoice[];
};

/**
 * Every modification schema used by any item in the add-on, deduplicated
 * (items share schemas, e.g. "Milk" on every drink), in order of first
 * appearance. Choices are sorted by seq.
 */
export function offeredModificationSchemas(
  tiers: ReadonlyArray<ComboAddonTier>,
  items: MenuItemIndex
): OfferedModificationSchema[] {
  const schemas = new Map<string, ItemModificationSchema>();
  const tiersBySchema = new Map<string, Set<string>>();
  for (const tier of tiers) {
    for (const ti of tier.items) {
      const ref = items.get(ti.item_schema_id);
      for (const ms of ref?.item.modification_schemas ?? []) {
        if (!schemas.has(ms.id)) {
          schemas.set(ms.id, ms);
          tiersBySchema.set(ms.id, new Set());
        }
        tiersBySchema.get(ms.id)?.add(tier.id);
      }
    }
  }

  const tierOrder = tiers.map((t) => t.id);
  return [...schemas.values()].map((schema) => {
    const tierSet = tiersBySchema.get(schema.id) ?? new Set<string>();
    const tierIds = tierOrder.filter((id) => tierSet.has(id));
    const choices = [...(schema.choices ?? [])]
      .sort((a, b) => a.seq - b.seq)
      .map((choice) => ({ choice, tierIds }));
    return { schema, choices };
  });
}

/**
 * Converts the editor's override inputs into the API's modification_prices.
 * Blank or unparseable inputs mean "no override" and aren't saved. Overrides
 * the backend would reject because nothing in their scope offers the choice
 * any more (an item or tier was removed after the override was entered) are
 * dropped.
 */
export function inputsToOverrides(
  inputs: OverrideInputs,
  tiers: ReadonlyArray<ComboAddonTier>,
  items: MenuItemIndex
): ComboAddonModificationPrice[] {
  const offered = offeredChoicesByTier(tiers, items);
  const offeredAnywhere = new Set<string>();
  offered.forEach((choices) => choices.forEach((_, id) => offeredAnywhere.add(id)));

  const result: ComboAddonModificationPrice[] = [];
  for (const [key, raw] of Object.entries(inputs)) {
    const cents = dollarsToCents(raw);
    if (cents === null) {
      continue;
    }
    const sep = key.indexOf("|");
    const scope = key.slice(0, sep);
    const choiceId = key.slice(sep + 1);
    if (scope === ALL_TIERS) {
      if (offeredAnywhere.has(choiceId)) {
        result.push({ item_modification_choice_id: choiceId, price_adjustment: cents });
      }
    } else if (offered.get(scope)?.has(choiceId)) {
      result.push({
        combo_addon_tier_id: scope,
        item_modification_choice_id: choiceId,
        price_adjustment: cents,
      });
    }
  }
  return result;
}

export type AdjustmentSource = "tier" | "all_tiers" | "regular";

export type EffectiveAdjustment = {
  cents: number;
  source: AdjustmentSource;
};

/**
 * The adjustment a choice has inside one tier of an add-on. The most specific
 * override wins: the tier-scoped row, then the add-on-wide row, then the
 * choice's regular adjustment.
 */
export function effectiveAdjustment(
  overrides: ReadonlyArray<ComboAddonModificationPrice>,
  tierId: string,
  choiceId: string,
  regularCents: number
): EffectiveAdjustment {
  let allTiers: number | undefined;
  for (const mp of overrides) {
    if (mp.item_modification_choice_id !== choiceId) {
      continue;
    }
    if (mp.combo_addon_tier_id === tierId) {
      return { cents: mp.price_adjustment, source: "tier" };
    }
    if (!mp.combo_addon_tier_id) {
      allTiers = mp.price_adjustment;
    }
  }
  if (allTiers !== undefined) {
    return { cents: allTiers, source: "all_tiers" };
  }
  return { cents: regularCents, source: "regular" };
}

export type NegativePriceError = {
  tierId: string;
  choiceId: string;
  // The effective adjustment and where it came from.
  adjustment: number;
  source: AdjustmentSource;
  tierPrice: number;
};

/**
 * Overrides that would make a tier's price negative. Mirrors the backend:
 * for every tier and every choice its items offer, the effective override
 * (tier-scoped, else add-on-wide) must keep tier.price + adjustment >= 0.
 * Choices without an override keep their regular adjustment, which the
 * add-on doesn't control, so they aren't checked.
 */
export function findNegativePriceErrors(
  addon: Pick<ComboAddonSchema, "tiers" | "modification_prices">,
  items: MenuItemIndex
): NegativePriceError[] {
  const offered = offeredChoicesByTier(addon.tiers, items);
  const errors: NegativePriceError[] = [];
  for (const tier of addon.tiers) {
    offered.get(tier.id)?.forEach((choice, choiceId) => {
      const eff = effectiveAdjustment(
        addon.modification_prices,
        tier.id,
        choiceId,
        choice.price_adjustment
      );
      if (eff.source !== "regular" && tier.price + eff.cents < 0) {
        errors.push({
          tierId: tier.id,
          choiceId,
          adjustment: eff.cents,
          source: eff.source,
          tierPrice: tier.price,
        });
      }
    });
  }
  return errors;
}

export type TierOptionPrice = {
  choice: ItemModificationChoice;
  cents: number;
  source: AdjustmentSource;
};

/**
 * The options of a tier whose price differs from their regular adjustment
 * because an override is in effect, for the per-tier summary
 * ("Oat milk free, Extra boba +$0.50").
 */
export function tierOverriddenOptions(
  addon: Pick<ComboAddonSchema, "tiers" | "modification_prices">,
  tierId: string,
  items: MenuItemIndex
): TierOptionPrice[] {
  const tier = addon.tiers.find((t) => t.id === tierId);
  if (!tier) {
    return [];
  }
  const offered = offeredChoicesByTier([tier], items).get(tierId) ?? new Map();
  const result: TierOptionPrice[] = [];
  offered.forEach((choice, choiceId) => {
    const eff = effectiveAdjustment(
      addon.modification_prices,
      tierId,
      choiceId,
      choice.price_adjustment
    );
    if (eff.source !== "regular") {
      result.push({ choice, cents: eff.cents, source: eff.source });
    }
  });
  return result;
}

// ---------------------------------------------------------------------------
// Tier editing
// ---------------------------------------------------------------------------

export function moveInArray<T>(arr: ReadonlyArray<T>, from: number, to: number): T[] {
  const next = [...arr];
  if (from < 0 || from >= next.length || to < 0 || to >= next.length) {
    return next;
  }
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** Copy of a tier (items, price, points, and its tier-scoped overrides), inserted after it. */
export function duplicateTier(
  tiers: ReadonlyArray<ComboAddonTier>,
  inputs: OverrideInputs,
  tierId: string,
  newId: string,
  copyNameSuffix: string
): { tiers: ComboAddonTier[]; inputs: OverrideInputs } {
  const index = tiers.findIndex((t) => t.id === tierId);
  if (index < 0) {
    return { tiers: [...tiers], inputs };
  }
  const source = tiers[index];
  const copy: ComboAddonTier = {
    ...source,
    id: newId,
    name: source.name ? `${source.name}${copyNameSuffix}` : source.name,
    items: source.items.map((ti) => ({ item_schema_id: ti.item_schema_id, seq: ti.seq })),
  };
  const nextTiers = [...tiers];
  nextTiers.splice(index + 1, 0, copy);

  const nextInputs: OverrideInputs = { ...inputs };
  const prefix = `${tierId}|`;
  for (const [key, value] of Object.entries(inputs)) {
    if (key.startsWith(prefix)) {
      nextInputs[`${newId}|${key.slice(prefix.length)}`] = value;
    }
  }
  return { tiers: nextTiers, inputs: nextInputs };
}

/**
 * Moves an item from one tier to the end of another. If the target already
 * has the item (an item may appear only once per tier), it's just removed
 * from the source.
 */
export function moveItemToTier(
  tiers: ReadonlyArray<ComboAddonTier>,
  fromTierId: string,
  itemSchemaId: string,
  toTierId: string
): ComboAddonTier[] {
  if (fromTierId === toTierId) {
    return [...tiers];
  }
  const target = tiers.find((t) => t.id === toTierId);
  if (!target) {
    return [...tiers];
  }
  const alreadyThere = target.items.some((ti) => ti.item_schema_id === itemSchemaId);
  return tiers.map((t) => {
    if (t.id === fromTierId) {
      return { ...t, items: t.items.filter((ti) => ti.item_schema_id !== itemSchemaId) };
    }
    if (t.id === toTierId && !alreadyThere) {
      return {
        ...t,
        items: [...t.items, { item_schema_id: itemSchemaId, seq: t.items.length }],
      };
    }
    return t;
  });
}

/**
 * The add-on as sent to the API: trimmed names, seq from list order,
 * blank points omitted (falls back to the restaurant config), and override
 * inputs converted (see inputsToOverrides).
 */
export function addonForSave(
  draft: ComboAddonSchema,
  inputs: OverrideInputs,
  items: MenuItemIndex
): ComboAddonSchema {
  const tiers: ComboAddonTier[] = draft.tiers.map((t, i) => {
    const tier: ComboAddonTier = {
      id: t.id,
      name: (t.name ?? "").trim(),
      price: t.price,
      seq: i,
      items: t.items.map((ti, j) => ({ item_schema_id: ti.item_schema_id, seq: j })),
    };
    if (t.points !== null && t.points !== undefined) {
      tier.points = t.points;
    }
    return tier;
  });
  return {
    ...(draft.id ? { id: draft.id } : {}),
    menu_type: draft.menu_type,
    name: draft.name.trim(),
    intl_name: cleanIntlName(draft.intl_name),
    tiers,
    modification_prices: inputsToOverrides(inputs, tiers, items),
  };
}

/** The combo as sent to the API, with seq from list order. */
export function comboForSave(draft: ComboSchema): ComboSchema {
  return {
    ...draft,
    name: draft.name.trim(),
    intl_name: cleanIntlName(draft.intl_name),
    entrees: draft.entrees.map((e, i) => ({ item_schema_id: e.item_schema_id, seq: i })),
    addons: draft.addons.map((a, i) => ({
      combo_addon_schema_id: a.combo_addon_schema_id,
      required: a.required,
      seq: i,
    })),
  };
}

function cleanIntlName(
  intl: { [lang: string]: string } | undefined
): { [lang: string]: string } | undefined {
  if (!intl) {
    return undefined;
  }
  const out: { [lang: string]: string } = {};
  for (const [lang, value] of Object.entries(intl)) {
    if (value && value.trim()) {
      out[lang] = value.trim();
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

// ---------------------------------------------------------------------------
// Points
// ---------------------------------------------------------------------------

/**
 * The restaurant default for something with no points of its own, mirroring
 * backend points.EffectivePoints: only when the redeem-rate toggle is on and a
 * rate is set. Undefined means not redeemable.
 */
export function fallbackPoints(
  priceCents: number,
  config: PointsConfig | undefined
): number | undefined {
  if (!config?.enabled) {
    return undefined;
  }
  return effectivePoints(priceCents, config.rate_per_dollar ?? undefined);
}

/** A tier's own points, or the restaurant default applied to its price. */
export function effectiveTierPoints(
  tier: Pick<ComboAddonTier, "points" | "price">,
  config: PointsConfig | undefined
): number | undefined {
  if (tier.points !== null && tier.points !== undefined) {
    return tier.points;
  }
  return fallbackPoints(tier.price, config);
}

/** An item's own points, or the restaurant default applied to its price. */
export function effectiveItemPoints(
  item: Pick<ItemSchema, "points" | "price">,
  config: PointsConfig | undefined
): number | undefined {
  if (item.points !== null && item.points !== undefined) {
    return item.points;
  }
  return fallbackPoints(item.price, config);
}

// ---------------------------------------------------------------------------
// Combo preview
// ---------------------------------------------------------------------------

export type NonRedeemableReason =
  | { kind: "entree"; itemName: string }
  | { kind: "addon_tier"; addonName: string; tier: ComboAddonTier; tierIndex: number };

export type ComboPreview = {
  // Cheapest combination: cheapest entree + cheapest tier of each required
  // add-on, without modifications. Null without any entree.
  startingPrice: number | null;
  // Most expensive combination: priciest entree + priciest tier of every
  // add-on (optional ones included), without modifications.
  maxPrice: number | null;
  // Same as startingPrice over redeemable options only. Null if no
  // combination can be redeemed with points.
  startingPoints: number | null;
  // Entrees and required add-on tiers that can't be redeemed with points.
  nonRedeemable: NonRedeemableReason[];
};

/**
 * Live preview for the combo editor. Same rules as the backend's
 * starting_price / starting_points (rest/menu_combo.go), plus the max price.
 * Add-ons or tiers whose items aren't on the menu are ignored.
 */
export function computeComboPreview(
  combo: Pick<ComboSchema, "entrees" | "addons">,
  addons: ReadonlyMap<string, Pick<ComboAddonSchema, "name" | "tiers">>,
  items: MenuItemIndex,
  config: PointsConfig | undefined
): ComboPreview {
  const nonRedeemable: NonRedeemableReason[] = [];

  let minPrice: number | null = null;
  let maxPrice: number | null = null;
  let minPoints: number | null = null;
  for (const e of combo.entrees) {
    const ref = items.get(e.item_schema_id);
    if (!ref) {
      continue;
    }
    const price = ref.item.price;
    minPrice = minPrice === null ? price : Math.min(minPrice, price);
    maxPrice = maxPrice === null ? price : Math.max(maxPrice, price);
    const pts = effectiveItemPoints(ref.item, config);
    if (pts === undefined) {
      nonRedeemable.push({ kind: "entree", itemName: ref.item.name });
    } else {
      minPoints = minPoints === null ? pts : Math.min(minPoints, pts);
    }
  }
  if (minPrice === null || maxPrice === null) {
    return { startingPrice: null, maxPrice: null, startingPoints: null, nonRedeemable };
  }

  let startingPrice = minPrice;
  let max = maxPrice;
  let startingPoints = minPoints;
  for (const link of combo.addons) {
    const addon = addons.get(link.combo_addon_schema_id);
    if (!addon) {
      continue;
    }
    const tiers = addon.tiers.filter((t) => t.items.some((ti) => items.has(ti.item_schema_id)));
    if (tiers.length === 0) {
      continue;
    }
    const prices = tiers.map((t) => t.price);
    max += Math.max(...prices);
    if (!link.required) {
      continue;
    }
    startingPrice += Math.min(...prices);

    let minTierPoints: number | null = null;
    for (const tier of tiers) {
      const pts = effectiveTierPoints(tier, config);
      if (pts === undefined) {
        nonRedeemable.push({
          kind: "addon_tier",
          addonName: addon.name,
          tier,
          tierIndex: addon.tiers.indexOf(tier),
        });
      } else {
        minTierPoints = minTierPoints === null ? pts : Math.min(minTierPoints, pts);
      }
    }
    startingPoints =
      startingPoints === null || minTierPoints === null ? null : startingPoints + minTierPoints;
  }

  return { startingPrice, maxPrice: max, startingPoints, nonRedeemable };
}
